import * as Yup from "yup";

// login validation schema
export const loginSchema = Yup.object({
  email: Yup.string().email("Invalid Email").required("Email is required"),
  password: Yup.string().required("Password is required"),
});

// change password schema
export const changePasswordValidate = Yup.object({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string().required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword")], "Passwords must match")
    .required("Confirm password is required"),
});

// crop validation schema start
const numberField = (label: string) =>
  Yup.number()
    .typeError(`${label} must be a number`)
    .required(`${label} is required`)
    .min(0, `${label} must be greater than or equal to 0`);

export const CropValidationSchema = Yup.object().shape({
  crop_name: Yup.string().required("Crop name is required"),

  crop_name_hi: Yup.string().nullable(),

  crop_name_gu: Yup.string().nullable(),
});

// crop validation schema end

// equipment Validation Schema
export const EquipmentValidationSchema = Yup.object().shape({
  equipment_name: Yup.string().required("Equipment name is required"),
});

// equipment Validation Schema
export const ProblemValidationSchema = Yup.object().shape({
  problem_name: Yup.string().required("Problem name is required"),
});

export const moneyReceiveValidationSchema = Yup.object().shape({
  received_amount: Yup.number()
    .typeError("Amount must be a number")
    .positive("Amount must be greater than zero")
    .required("Received amount is required"),
});

// service validation schema
export const serviceValidationSchema = Yup.object().shape({
  service_name: Yup.string().required("Service name is required"),
  price: Yup.number()
    .typeError("Price must be a number")
    .positive("Price must be positive")
    .required("Price is required"),
  operator_price: Yup.number()
    .typeError("Operator Price must be a number")
    .positive("Operator Price must be positive")
    .required("Operator Price is required"),
  photo: Yup.mixed().required("Photo is required"),
  description: Yup.string().required("Description is required"),
  price_on: Yup.string().required("Price on is required"),
});

// service Item validation schema
export const serviceItemValidationSchema = Yup.object().shape({
  item_type: Yup.string().required("Please select a item type"),

  // item_price: Yup.number()
  //   .typeError("Item Price must be a number")
  //   .positive("Item Price must be positive")
  //   .required("Item Price is required"),

  item_name: Yup.string().required("Item name is required"),
});

// annouce mnet validate
export const announceValidation = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  message: Yup.string().required("Message is required"),
});

// operator validation schema

export const operatorValidationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  email: Yup.string()
    .required("Email is required")
    .email("Invalid email address"),
  mobile: Yup.string()
    .required("Phone number is required")
    .matches(/^[0-9]{10}$/, "Phone number must be exactly 10 digits"),
  type_of_equipment: Yup.array()
    .of(Yup.string())
    .min(1, "Select at least one equipment"),
});

// Booking Slots Schema
export const BookingSlotsSchema = Yup.object({
  slot: Yup.string().required("Please select a shift"),
  startTime: Yup.date().nullable().required("Start time is required"),
  endTime: Yup.date()
    .nullable()
    .required("End time is required")
    .test(
      "is-after-start",
      "End time must be after start time",
      function (value) {
        const { startTime } = this.parent;
        return value && startTime ? value > startTime : true;
      }
    )
    .test(
      "not-equal",
      "End time must not be equal to start time",
      function (value) {
        const { startTime } = this.parent;
        return value && startTime
          ? value.getTime() !== startTime.getTime()
          : true;
      }
    ),
});

// assign operator schema
export const AssignOperatorSchema = Yup.object().shape({
  operator_id: Yup.array().when(
    ["booking_status", "isReschedule", "operator_name"],
    {
      is: (
        booking_status: string,
        isReschedule: boolean,
        operator_name: string
      ) => {
        return !operator_name;
      },
      then: (schema) => {
        return schema
          .of(Yup.mixed().required("Invalid operator"))
          .min(1, "Please select at least one operator")
          .required("Please select at least one operator");
      },
      otherwise: (schema) => {
        return schema.notRequired();
      },
    }
  ),
  selected_date: Yup.date().required("Date is required"),
});

// add booking validate without validate
export const validateBooking = (values: any, hasSlot: boolean): any => {
  const errors: any = {};

  //Top-level fields
  if (!values.user_id) errors.user_id = "Farmer is required";
  if (!values.service_id) errors.service_id = "Service is required";
  if (!values.booking_date) errors.booking_date = "Booking date is required";

  if (hasSlot) {
    if (!values.timePeriod) errors.timePeriod = "Time period is required";
    if (!values.time_slot_id) errors.time_slot_id = "Time slot is required";
  }

  // Farm array
  if (!Array.isArray(values.farm_data) || values.farm_data.length === 0) {
    errors.farm_data = "At least one farm is required";
  } else {
    values.farm_data.forEach((farm: any, fIdx: number) => {
      const farmErr: any = {};

      if (!farm.farm_id) farmErr.farm_id = "Farm is required";

      // Crop array
      if (!Array.isArray(farm.crop_data) || farm.crop_data.length === 0) {
        farmErr.crop_data = "At least one crop is required per farm";
      } else {
        farm.crop_data.forEach((crop: any, cIdx: number) => {
          const cropErr: any = {};

          if (Number(values.service_id) !== 2) {
            if (!crop.crop_id) cropErr.crop_id = "Crop is required";

            if (!crop.variety_name?.trim())
              cropErr.variety_name = "Variety name is required";

            const area = Number(crop.area);
            if (isNaN(area) || area <= 0)
              cropErr.area = "Area must be a number greater than 0";

            if (!crop.sowing_date?.trim())
              cropErr.sowing_date = "Crop age (sowing date) is required";

            if (!crop.spray_for_problem?.trim())
              cropErr.spray_for_problem = "Spray for problem is required";
          }

          // Service-item arrays
          // Spray items are required only when service_id === 1
          if (Number(values.service_id) === 1) {
            if (
              !Array.isArray(crop.service_items_spray) ||
              crop.service_items_spray.length === 0
            )
              cropErr.service_items_spray = "Spray items are required";
          }

          // Soil & Additional are required only when service_id === 2
          if (Number(values.service_id) === 2) {
            if (
              !Array.isArray(crop.service_items_soil) ||
              crop.service_items_soil.length === 0
            )
              cropErr.service_items_soil = "Soil test selection is required";

            if (
              !Array.isArray(crop.service_items_additional) ||
              crop.service_items_additional.length === 0
            )
              cropErr.service_items_additional =
                "Additional test selection is required";
          }

          // If any crop errors → push them under the correct path
          if (Object.keys(cropErr).length > 0) {
            if (!farmErr.crop_data) farmErr.crop_data = [];
            farmErr.crop_data[cIdx] = cropErr;
          }
        });
      }

      // If any farm errors → push them under the correct path
      if (Object.keys(farmErr).length > 0) {
        if (!errors.farm_data) errors.farm_data = [];
        errors.farm_data[fIdx] = farmErr;
      }
    });
  }

  return errors;
};

// wallet point validate
export const walletSchema = Yup.object().shape({
  wallet_points: Yup.number()
    .typeError("Wallet points must be a number")
    .min(0, "Points cannot be negative")
    .moreThan(0, "Wallet points must be greater than zero")
    .required("Wallet points are required"),
});


const requiredNumber = Yup.number()
  .typeError("This field is required")
  .required("This field is required");

const requiredString = Yup.string().trim().required("This field is required");

const rowSchema = Yup.object().shape({
  parameter: requiredString,
  source: requiredString,
  dose: requiredString,
  method: requiredString,
});

// validation function for Soil Parameter START

const recommendationRowSchema = Yup.object().shape({
  parameter: Yup.mixed().required("Parameter is required"),
  source: Yup.string().required("Source is required"),
  dose: Yup.string().required("Dose is required"),
  method: Yup.string().required("Method is required"),
});

const advisoryRowSchema = Yup.object().shape({
  parameter: Yup.mixed().required("Parameter is required"),
  issue_identified: Yup.string().required("Issue is required"),
  recommended_advisory: Yup.string().required("Advisory is required"),
});

export const SoilTestValidationSchema = Yup.object().shape({
  // First 8 fields
  // n_from: requiredNumber,
  // n_to: requiredNumber,
  // p_from: requiredNumber,
  // p_to: requiredNumber,
  // k_from: requiredNumber,
  // k_to: requiredNumber,
  // ph_from: requiredNumber,
  // ph_to: requiredNumber,

  // Remaining
  // ec: requiredNumber,
  // oc_from: requiredNumber,
  // oc_to: requiredNumber,

  // Recommendation
  soil_recommendation: Yup.object().shape({
    low: Yup.array().of(recommendationRowSchema),
    medium: Yup.array().of(recommendationRowSchema),
    high: Yup.array().of(recommendationRowSchema),
  }),

   // Advisory
  soil_advisory: Yup.object().shape({
    low: Yup.array().of(advisoryRowSchema),
    medium: Yup.array().of(advisoryRowSchema),
    high: Yup.array().of(advisoryRowSchema),
  }),
});

// validation function for Soil Parameter END